import { useState, useEffect, useRef } from "react";
import { BsFillPatchCheckFill } from "react-icons/bs";
import { MdInsertPhoto } from "react-icons/md";
import { FaArrowLeft, FaLock } from "react-icons/fa";
import { socket } from "../../socket/socket";
import { getFileType, getMediaUrl, getProfileImage, groupMessagesByDate } from "../../utils/helpers";
import PremiumContentModal from "../../components/Chat/PremiumContentModal";
import PhotoViewer from "../../components/Profile/PhotoViewer";
import { GetChatMediaSignedUrl, uploadMultipleFilesToSignedUrls } from "../../services/api";
import { useSelector } from "react-redux";
import { CHAT_EVENTS } from "../../socket/socketEvents";

export default function ChatWindow({
    activeChat,
    messages,
    setMessages,
    loadingMessages,
    messageListRef,
    user,
    onBack,
    loadOlderMessages,
    hasMore,
    loadingMore
}) {
    const [input, setInput] = useState("");

    const [openPremium, setOpenPremium] = useState(false);
    const [premiumPrice, setPremiumPrice] = useState(0);
    const [premiumMediaCount, setPremiumMediaCount] = useState(0);

    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);

    const [uploading, setUploading] = useState(false);

    const dateRefs = useRef({});
    const bottomRef = useRef(null);
    const [activeDate, setActiveDate] = useState("");

    const fileRef = useRef(null);

    const groupedMessages = groupMessagesByDate(messages);

    const remoteStatus = useSelector((state) => state?.message?.remoteStatus);
    const remoteId = useSelector((state) => state?.message?.remoteId);

    useEffect(() => {
        const container = messageListRef.current;
        if (!container) return;

        const onScroll = () => {
            if (
                container.scrollTop <= 80 &&
                hasMore &&
                !loadingMore
            ) {
                loadOlderMessages();
            }
        };

        container.addEventListener("scroll", onScroll);
        return () => container.removeEventListener("scroll", onScroll);
    }, [activeChat, hasMore, loadingMore, loadOlderMessages]);

    useEffect(() => {
        const container = messageListRef.current;
        if (!container) return;

        const isNearBottom =
            container.scrollHeight - container.scrollTop - container.clientHeight < 150;

        if (isNearBottom) {
            bottomRef.current?.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, activeChat]);

    useEffect(() => {
        const container = messageListRef.current;
        if (!container) return;

        const onScroll = () => {
            const entries = Object.entries(dateRefs.current);

            for (let i = entries.length - 1; i >= 0; i--) {
                const [date, el] = entries[i];
                if (el && el.getBoundingClientRect().top <= 140) {
                    setActiveDate(date);
                    break;
                }
            }
        };

        container.addEventListener("scroll", onScroll);
        return () => container.removeEventListener("scroll", onScroll);
    }, []);

    const sendMessage = () => {
        if (!input.trim()) return;

        const tempId = `temp-${Date.now()}`;

        const tempMsg = {
            _id: tempId,
            tempId,
            senderId: user?._id,
            receiverId: remoteId,
            conversationId: activeChat.conversationId,
            createdAt: new Date().toISOString(),
            payload: {
                text: input,
                media: [],
            },
        };

        setMessages((prev) => [...prev, tempMsg]);
        setInput("");
        console.log("Send Message ", tempMsg);
        socket.emit(CHAT_EVENTS.CHAT_SEND_MESSAGE, tempMsg);
    };

    const handleUnlock = (chat) => {
        setPremiumPrice(chat?.price);
        setPremiumMediaCount(chat?.payload?.media?.length || 0);
        setOpenPremium(true);
    };

    const openPhotoViewer = (media) => {
        setViewerImages(
            media.map((m, i) => ({
                id: i,
                url: `${process.env.REACT_APP_MEDIA_BASE_URL}${m?.url}`,
            }))
        );
        setActiveIndex(0);
        setViewerOpen(true);
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;

        const tempId = `temp-${Date.now()}`;

        // 🔹 Local previews
        const tempMedia = files.map((file) => ({
            type: getFileType(file), // image | video
            url: URL.createObjectURL(file),
            isLocal: true,
        }));

        const tempMsg = {
            _id: tempId,
            tempId,
            senderId: user?._id,
            receiverId: remoteId,
            conversationId: activeChat.conversationId,
            createdAt: new Date().toISOString(),
            uploading: true,
            payload: {
                text: "",
                media: tempMedia,
            },
        };

        setMessages((prev) => [...prev, tempMsg]);
        setUploading(true);

        try {
            // 1️⃣ Get signed URLs
            const res = await GetChatMediaSignedUrl(
                getFileType(files[0]), // backend usually needs one type
                files.length
            );

            const signedData = res.data; // [{ signedUrl, key }]

            // 2️⃣ Upload files
            await uploadMultipleFilesToSignedUrls(
                signedData.map((d) => d.signedUrl),
                files
            );

            // 3️⃣ Final media payload
            const uploadedMedia = signedData.map((d, idx) => ({
                type: getFileType(files[idx]),
                url: d.key, // store RELATIVE path
            }));

            // 4️⃣ Update UI (replace previews)
            setMessages((prev) =>
                prev.map((m) =>
                    m.tempId === tempId
                        ? {
                            ...m,
                            uploading: false,
                            payload: {
                                ...m.payload,
                                media: uploadedMedia,
                            },
                        }
                        : m
                )
            );

            // 5️⃣ Emit socket event
            socket.emit(CHAT_EVENTS.CHAT_SEND_MESSAGE, {
                tempId,
                receiverId: remoteId,
                conversationId: activeChat.conversationId,
                payload: {
                    text: "",
                    media: uploadedMedia,
                },
            });

            // 6️⃣ Cleanup previews
            tempMedia.forEach((m) => URL.revokeObjectURL(m.url));
        } catch (err) {
            console.error(err);

            // Optional: mark failed
            setMessages((prev) =>
                prev.map((m) =>
                    m.tempId === tempId
                        ? { ...m, uploading: false, failed: true }
                        : m
                )
            );
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    if (!activeChat) {
        return (
            <div className="flex-1 h-full flex items-center justify-center text-gray-400">
                <div className="text-center">
                    <h2 className="text-lg font-semibold text-gray-300">
                        Start chatting
                    </h2>
                    <p className="text-sm">
                        Please select a chat to start messaging
                    </p>
                </div>
            </div>
        );
    }

    return (
        <main className="flex flex-col h-full text-gray-200">

            <div className="h-[1px] bg-gray-800 w-full" />

            <div className="py-3 px-4 border-b border-gray-800 flex items-center gap-3">
                <button onClick={onBack} className="sm:hidden">
                    <FaArrowLeft />
                </button>

                <img
                    src={getProfileImage(getMediaUrl(activeChat?.remoteUser?.ppImageURL))}
                    className="w-9 h-9 rounded-full"
                    alt=""
                />

                <div>
                    <div className="font-semibold text-sm flex items-center gap-2">
                        {activeChat?.remoteUser?.name}
                        {activeChat?.remoteUser?.is_verified && (
                            <BsFillPatchCheckFill className="text-blue-600" />
                        )}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-gray-400">
                        <span
                            className={`w-2 h-2 rounded-full ${remoteStatus === "online" ? "bg-green-500" : "bg-red-500"}`}
                        />
                        {remoteStatus === "online" ? "Online" : "Offline"}
                    </div>
                </div>
            </div>

            <div
                ref={messageListRef}
                onContextMenu={(e) => e.preventDefault()}
                className="flex-1 overflow-y-auto px-4 py-3"
            >
                {activeDate && (
                    <div className="sticky top-0 z-10 flex justify-center my-2">
                        <span className="bg-gray-800 text-xs px-3 py-1 rounded-full text-gray-300">
                            {activeDate}
                        </span>
                    </div>
                )}

                {loadingMessages ? (
                    <div className="text-center text-gray-400">Loading...</div>
                ) : (
                    Object.entries(groupedMessages).map(([date, msgs]) => (
                        <div key={date} ref={(el) => (dateRefs.current[date] = el)}>

                            <div className="flex justify-center mb-4">
                                <span className="text-xs text-gray-400 bg-gray-800 px-3 py-1 rounded-full">
                                    {date}
                                </span>
                            </div>

                            {msgs.map((m) => {
                                const mediaCount = m?.payload?.media?.length || 0;

                                const gridClass =
                                    mediaCount === 1
                                        ? "flex justify-center"
                                        : "grid grid-cols-2 gap-1";

                                return (
                                    <div
                                        key={m._id}
                                        className={`max-w-[35%] ${m?.senderId === user?._id ? "ml-auto text-right" : ""
                                            }`}
                                    >
                                        {m?.payload?.media?.length > 0 && (
                                            <div
                                                onClick={() =>
                                                    m?.locked
                                                        ? handleUnlock(m)
                                                        : openPhotoViewer(m?.payload?.media)
                                                }
                                                className={`relative overflow-hidden rounded-2xl cursor-pointer
                                                    ${m.payload.media.length === 1
                                                        ? "w-full"
                                                        : "grid grid-cols-2 gap-1"
                                                    }
                                                `}
                                            >
                                                {m.payload.media.slice(0, 4).map((media, idx) => {
                                                    const src = media.isLocal
                                                        ? media.url
                                                        : `${process.env.REACT_APP_MEDIA_BASE_URL}${media.url}`;

                                                    return (
                                                        <div
                                                            key={idx}
                                                            className="relative aspect-square w-full bg-black"
                                                        >
                                                            {media.type === "image" ? (
                                                                <img
                                                                    src={src}
                                                                    alt=""
                                                                    className={`w-full h-full object-cover ${m.uploading ? "opacity-50 blur-sm" : m.locked ? "blur-xl" : ""
                                                                        }`}
                                                                />
                                                            ) : (
                                                                <video
                                                                    src={src}
                                                                    muted
                                                                    playsInline
                                                                    preload="metadata"
                                                                    className={`w-full h-full object-cover ${m.uploading ? "opacity-50 blur-sm" : m.locked ? "blur-xl" : ""
                                                                        }`}
                                                                />
                                                            )}

                                                            {/* ▶ Play icon */}
                                                            {media.type === "video" && (
                                                                <div className="absolute inset-0 flex items-center justify-center">
                                                                    <div className="bg-black/60 rounded-full p-3 text-white">
                                                                        ▶
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}

                                                {/* LOCK OVERLAY */}
                                                {m?.locked && (
                                                    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-md">
                                                        <div className="flex flex-col items-center gap-1">
                                                            <FaLock className="text-yellow-400" />
                                                            <span className="text-xs text-yellow-400">PREMIUM</span>
                                                            <span className="text-xs font-semibold">{m?.price}</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {m?.payload?.text && (
                                            <div
                                                className="inline-flex text-left bg-gray-700 px-3 py-2 text-sm leading-[1.35] rounded-lg w-fit max-w-full break-words mt-1"
                                            >
                                                {m.payload.text}
                                            </div>
                                        )}

                                        <div className="text-[10px] text-gray-500 mt-1 mb-2">
                                            {new Date(m.createdAt).toLocaleTimeString([], {
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}

                <div ref={bottomRef} />
            </div>

            <div className="p-3 border-t border-gray-800">
                <div className="flex gap-2 items-center">
                    <button
                        onClick={() => fileRef.current?.click()}
                        className="p-2 bg-gray-900 rounded-lg"
                    >
                        <MdInsertPhoto />
                    </button>

                    <input
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                sendMessage();
                            }
                        }}
                        className="flex-1 bg-gray-900 rounded-xl px-4 py-2 text-sm"
                        placeholder="Type a message..."
                    />

                    <button
                        onClick={sendMessage}
                        disabled={uploading}
                        className={`w-10 h-10 rounded-full ${uploading ? "bg-gray-600" : "bg-red-500"
                            }`}
                    >
                        ➤
                    </button>
                </div>

                <input
                    ref={fileRef}
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                />
            </div>

            {loadingMore && (
                <div className="text-center text-xs text-gray-400 py-2">
                    Loading earlier messages…
                </div>
            )}

            <PremiumContentModal
                isOpen={openPremium}
                onClose={() => setOpenPremium(false)}
                onSend={() => setOpenPremium(false)}
                price={premiumPrice}
                mediaCount={premiumMediaCount}
            />

            {viewerOpen && (
                <PhotoViewer
                    images={viewerImages}
                    currentIndex={activeIndex}
                    onClose={() => setViewerOpen(false)}
                    onPrev={() => setActiveIndex((i) => i - 1)}
                    onNext={() => setActiveIndex((i) => i + 1)}
                />
            )}
        </main>
    );
}